import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  Loader2,
  Mail,
  MessageCircle,
  ReceiptText,
  X,
} from "lucide-react";
import { trackBookingEvent, trackPaymentSuccessOnce } from "@/lib/bookingAnalytics";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;
const BOOKING_ENDPOINT = `${API}/bookings`;
const STORAGE_KEY = "telefonka-booking-draft-v5";
const CONTACT_URL = "https://www.instagram.com/hellotelefonka/";

const money = (value) =>
  typeof value === "number" || (typeof value === "string" && value.trim() !== "")
    ? `${new Intl.NumberFormat("hu-HU").format(Number(value))} Ft`
    : "—";

const dateLabel = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    }).format(new Date(`${value}T12:00:00`));
  } catch (_) {
    return value;
  }
};

export const normalizeCheckoutStartError = (error) => {
  if (error?.response?.status !== 409) return error;

  // BookingFlow uses HTTP 409 specifically for a real device/date collision.
  // A Checkout endpoint can also return 409 (expired/already-paid/state race),
  // but that must never mark an otherwise available Telefonka as booked.
  const wrapped = new Error(
    error?.response?.data?.detail || error?.message || "A Stripe Checkout indítása sikertelen."
  );
  wrapped.response = {
    ...(error.response || {}),
    status: 502,
    data: error?.response?.data || { detail: wrapped.message },
  };
  wrapped.cause = error;
  return wrapped;
};

export default function PaymentExperience() {
  const [config, setConfig] = useState(null);
  const configRef = useRef(null);
  const [result, setResult] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [copied, setCopied] = useState(false);
  const trackedResultRef = useRef(null);
  const [returnMode] = useState(() =>
    new URLSearchParams(window.location.search).get("payment")
  );

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/payments/config`)
      .then(({ data }) => {
        if (!active) return;
        configRef.current = data || null;
        setConfig(data || null);
      })
      .catch(() => {
        if (!active) return;
        configRef.current = null;
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2400);
    return () => window.clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (returnMode) return undefined;
    const originalPost = axios.post;
    let active = true;

    axios.post = async function patchedPost(url, data, requestConfig) {
      const response = await originalPost.call(axios, url, data, requestConfig);
      const normalized = typeof url === "string" ? url.replace(/\/$/, "") : "";
      if (
        active &&
        normalized === BOOKING_ENDPOINT &&
        response?.data?.id
      ) {
        // Never let the asynchronous config request create a race where the
        // booking is inserted (and therefore reserves the device) but Checkout
        // is skipped because config has not arrived yet. Refresh config once when
        // needed; if it is still unknown, let the checkout endpoint be the source
        // of truth. Only an explicit `stripe_enabled: false` disables redirect.
        let paymentConfig = configRef.current;
        if (!paymentConfig) {
          try {
            const configResponse = await axios.get(`${API}/payments/config`);
            paymentConfig = configResponse?.data || null;
            if (active) {
              configRef.current = paymentConfig;
              setConfig(paymentConfig);
            }
          } catch (_) {
            paymentConfig = null;
          }
        }

        if (paymentConfig?.stripe_enabled === false) {
          return response;
        }

        try {
          const checkout = await originalPost.call(
            axios,
            `${API}/payments/stripe/checkout`,
            { booking_id: response.data.id }
          );
          if (checkout?.data?.url) {
            window.location.assign(checkout.data.url);
            // Keep the browser on the navigation path. This deliberately never
            // resolves before Stripe navigation, so BookingFlow cannot render a
            // local success state while the payment is still outstanding.
            return await new Promise(() => {});
          }
          throw new Error("A Stripe nem adott vissza fizetési hivatkozást.");
        } catch (error) {
          // The backend releases deterministic initial Checkout failures. A short
          // pending-hold cleanup also protects against interrupted hand-offs.
          // Do not leak a Checkout 409 back as a fake device/date conflict.
          console.error("Stripe Checkout indítása sikertelen", error);
          throw normalizeCheckoutStartError(error);
        }
      }
      return response;
    };

    return () => {
      active = false;
      axios.post = originalPost;
    };
  }, [returnMode]);

  useEffect(() => {
    if (!returnMode) return undefined;
    const params = new URLSearchParams(window.location.search);
    if (returnMode === "cancelled") {
      setResult({
        state: "cancelled",
        data: { booking_id: params.get("booking_id") || null },
      });
      return undefined;
    }

    const sessionId = params.get("session_id");
    if (returnMode !== "success" || !sessionId) {
      setResult({ state: "error" });
      return undefined;
    }

    let active = true;
    let timer;
    let attempts = 0;
    setResult({ state: "loading" });

    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/stripe/session-status`, {
          params: { session_id: sessionId },
        });
        if (!active) return;
        if (data?.payment_status === "paid") {
          const summary = await axios.get(`${API}/payments/stripe/success-summary`, {
            params: { session_id: sessionId },
          });
          if (!active) return;
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (_) {
            // Payment validity never depends on local browser storage.
          }
          setResult({ state: "paid", data: { ...data, ...(summary.data || {}) } });
          return;
        }
        if (["expired", "cancelled"].includes(data?.status)) {
          setResult({ state: "error", data });
          return;
        }
        attempts += 1;
        if (attempts < 6) timer = window.setTimeout(check, 1500);
        else setResult({ state: "processing", data });
      } catch (_) {
        attempts += 1;
        if (attempts < 4) timer = window.setTimeout(check, 1500);
        else setResult({ state: "error" });
      }
    };

    check();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [returnMode]);

  useEffect(() => {
    const state = result?.state;
    if (!state || !["paid", "processing", "error", "cancelled"].includes(state)) return;
    if (trackedResultRef.current === state) return;
    trackedResultRef.current = state;

    const data = result?.data || {};
    if (state === "paid") {
      const sessionId = new URLSearchParams(window.location.search).get("session_id");
      const deviceNames = Array.isArray(data.device_names) ? data.device_names.filter(Boolean) : [];
      trackPaymentSuccessOnce(sessionId, {
        payment_status: "paid",
        device_count: deviceNames.length || (data.device_name ? 1 : 0),
        extras_count: Number(data.extras_count || 0),
        amount_huf: Number(data.amount_huf || 0),
        currency: String(data.currency || "HUF").toUpperCase(),
      });
      return;
    }

    const eventByState = {
      processing: "payment_processing",
      error: "payment_failed",
      cancelled: "payment_cancelled",
    };
    trackBookingEvent(eventByState[state], {
      payment_status: data.payment_status || state,
      extras_count: Number(data.extras_count || 0),
      amount_huf: Number(data.amount_huf || 0),
      currency: String(data.currency || "HUF").toUpperCase(),
    });
  }, [result]);

  const cleanReturnUrl = (hash = "#booking") => {
    const url = new URL(window.location.href);
    url.searchParams.delete("payment");
    url.searchParams.delete("session_id");
    url.searchParams.delete("booking_id");
    window.location.assign(`${url.pathname}${url.search}${hash}`);
  };

  const retryCancelledPayment = async () => {
    const bookingId = new URLSearchParams(window.location.search).get("booking_id");
    if (!bookingId || retrying) return;
    setRetrying(true);
    try {
      const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
        booking_id: bookingId,
      });
      if (data?.url) window.location.assign(data.url);
      else setResult({ state: "error", data: { booking_id: bookingId } });
    } catch (_) {
      setResult({ state: "error", data: { booking_id: bookingId } });
    } finally {
      setRetrying(false);
    }
  };

  const copyBookingId = async () => {
    const bookingId = String(result?.data?.booking_id || "").trim();
    if (!bookingId || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(bookingId);
      setCopied(true);
    } catch (_) {
      setCopied(false);
    }
  };

  if (!returnMode) {
    if (!config?.stripe_enabled) return null;
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 w-[calc(100%-2rem)] max-w-xl pointer-events-none">
        <div className="rounded-2xl border border-[rgba(110,30,43,0.16)] bg-[rgba(250,246,239,0.96)] backdrop-blur-md shadow-lg px-4 py-3 flex items-center gap-3">
          <CreditCard
            size={18}
            className="text-[var(--burgundy)] shrink-0"
            aria-hidden="true"
          />
          <p className="text-xs sm:text-sm text-[var(--ink-soft)]">
            A foglalás elküldése után biztonságos Stripe fizetésre irányítunk. A fizetendő összeget a szerver ellenőrzi.
          </p>
        </div>
      </div>
    );
  }

  const paidDeviceNames = Array.isArray(result?.data?.device_names)
    ? result.data.device_names.filter(Boolean)
    : [];
  const deviceLabel = paidDeviceNames.length
    ? paidDeviceNames.join(" + ")
    : result?.data?.device_name || "Telefonka";
  const extraNames = Array.isArray(result?.data?.extra_names)
    ? result.data.extra_names.filter(Boolean)
    : [];

  return (
    <div
      id="payment-result"
      className="fixed inset-0 z-[100] overflow-y-auto overflow-x-hidden bg-[var(--beige-100)] px-4 sm:px-5 py-8 sm:py-12"
      role="dialog"
      aria-modal="true"
      aria-label="Fizetés eredménye"
    >
      <div className="max-w-3xl mx-auto tk-card p-5 sm:p-8 md:p-10 shadow-2xl">
        {(!result || result.state === "loading") && (
          <div role="status" aria-live="polite" className="text-center py-5">
            <Status
              icon={<Loader2 size={30} className="animate-spin" />}
              title="Foglalás ellenőrzése…"
              text="A fizetésed eredményét egyeztetjük a Stripe-pal és a foglalási rendszerrel."
            />
          </div>
        )}

        {result?.state === "paid" && (
          <div data-testid="payment-success-state">
            <div className="text-center">
              <Status
                icon={<CheckCircle2 size={30} />}
                title="Sikeres foglalás!"
                text="Köszönjük! A fizetésed sikeresen beérkezett, a Telefonka foglalásodat rögzítettük."
              />
            </div>

            <section className="mt-7 rounded-[26px] border border-[rgba(110,30,43,0.14)] bg-white p-4 sm:p-5" aria-labelledby="booking-confirmation-title">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <p id="booking-confirmation-title" className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">
                    Foglalási azonosító
                  </p>
                  <p className="mt-1 font-serif text-xl sm:text-2xl text-[var(--burgundy)] break-all">
                    {result.data?.booking_id || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={copyBookingId}
                  disabled={!result.data?.booking_id || !navigator.clipboard?.writeText}
                  aria-label="Foglalási azonosító másolása"
                  className="min-h-11 shrink-0 rounded-full border border-[rgba(110,30,43,0.18)] px-4 inline-flex items-center justify-center gap-2 text-sm text-[var(--burgundy)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)] disabled:opacity-40"
                >
                  {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                  {copied ? "Másolva" : "Másolás"}
                </button>
              </div>
              <span className="sr-only" role="status" aria-live="polite">
                {copied ? "Foglalási azonosító másolva." : ""}
              </span>
            </section>

            <section className="mt-4" aria-labelledby="booking-summary-title">
              <h3 id="booking-summary-title" className="font-serif text-2xl text-[var(--ink)]">
                Foglalásod összegzése
              </h3>
              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-left">
                <Tile label="Esemény dátuma" value={dateLabel(result.data?.event_date)} />
                <Tile label="Telefonka" value={deviceLabel} />
                <Tile
                  label="Kiválasztott extrák"
                  value={extraNames.length ? extraNames.join(" · ") : "Nem választottál fizetős extrát."}
                />
                <Tile label="Fizetett végösszeg" value={money(result.data?.amount_huf)} strong />
                <Tile label="Fizetési állapot" value="Fizetve" />
              </div>
            </section>

            <section className="mt-5 rounded-2xl bg-[rgba(110,30,43,0.05)] p-4 sm:p-5 flex gap-3" aria-labelledby="confirmation-email-title">
              <Mail size={19} className="text-[var(--burgundy)] shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h3 id="confirmation-email-title" className="font-medium text-sm text-[var(--ink)]">
                  Visszaigazoló e-mailre is számíthatsz.
                </h3>
                <p className="mt-1 text-sm text-[var(--ink-soft)] leading-relaxed">
                  Ha néhány percen belül nem érkezik meg, ellenőrizd a spam mappát, majd írj nekünk a foglalási azonosítódra hivatkozva.
                </p>
              </div>
            </section>

            <section className="mt-5 rounded-2xl border border-[rgba(110,30,43,0.12)] p-4 sm:p-5" aria-labelledby="next-steps-title">
              <h3 id="next-steps-title" className="font-serif text-2xl text-[var(--ink)]">Mi történik most?</h3>
              <div className="mt-4 grid gap-4">
                <NextStep number="1" title="A foglalásod rögzítve van" text="A sikeres fizetéshez tartozó foglalást visszaigazoltuk." />
                <NextStep number="2" title="Küldd el az üdvözlő üzeneted" text="Küldd el azt az üzenetet, amelyet a vendégeid a sípszó előtt hallani fognak." />
                <NextStep number="3" title="Az esemény előtt egyeztetünk" text="Az átvétellel vagy szállítással és a használattal kapcsolatos részleteket időben egyeztetjük veled." />
              </div>
            </section>

            <div className="mt-5 rounded-2xl bg-[var(--beige-50)] p-4 sm:p-5 flex gap-3">
              <ReceiptText size={18} className="text-[var(--burgundy)] shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
                {result.data?.invoice_status === "issued"
                  ? "A számla elkészült."
                  : result.data?.invoice_status === "error"
                    ? "A fizetés sikeres, de a számlázást külön ellenőriznünk kell. A foglalás ettől érvényes."
                    : "A fizetés sikeres; a számlázási folyamatot a foglaláshoz rögzítettük."}
              </p>
            </div>

            <section className="mt-5 text-center" aria-labelledby="success-contact-title">
              <MessageCircle size={20} className="mx-auto text-[var(--burgundy)]" aria-hidden="true" />
              <h3 id="success-contact-title" className="font-serif text-xl mt-2">Kérdésed van a foglalással kapcsolatban?</h3>
              <p className="mt-1 text-sm text-[var(--ink-soft)]">
                Írj nekünk, és hivatkozz a foglalási azonosítódra.
              </p>
            </section>

            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <button
                type="button"
                onClick={() => cleanReturnUrl("#top")}
                className="tk-btn-primary justify-center min-h-11"
              >
                Vissza a főoldalra
              </button>
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tk-btn-secondary justify-center min-h-11"
              >
                Kapcsolat Instagramon
              </a>
            </div>
          </div>
        )}

        {result?.state === "cancelled" && (
          <div className="text-center">
            <Status
              icon={<X size={30} />}
              title="A fizetés nem fejeződött be"
              text="A foglalás még nincs kifizetve. A meglévő fizetési munkamenetet biztonságosan megpróbálhatod folytatni, amíg az elérhető."
            />
            {result.data?.booking_id && (
              <p className="mt-4 text-xs text-[var(--ink-soft)] break-all">Foglalási azonosító: {result.data.booking_id}</p>
            )}
            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <button
                type="button"
                onClick={retryCancelledPayment}
                disabled={retrying}
                className="tk-btn-primary justify-center min-h-11 disabled:opacity-40"
              >
                {retrying ? "Fizetés megnyitása…" : "Fizetés folytatása"}
              </button>
              <button
                type="button"
                onClick={() => cleanReturnUrl()}
                className="tk-btn-secondary justify-center min-h-11"
              >
                Vissza az oldalra
              </button>
            </div>
          </div>
        )}

        {result?.state === "processing" && (
          <div role="status" aria-live="polite" className="text-center">
            <Status
              icon={<Loader2 size={30} className="animate-spin" />}
              title="A fizetés feldolgozása folyamatban van"
              text="A foglalásod adatait rögzítettük, a fizetés végleges Stripe-visszaigazolását még várjuk."
            />
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="tk-btn-primary mt-7 justify-center min-h-11"
            >
              Állapot újraellenőrzése
            </button>
          </div>
        )}

        {result?.state === "error" && (
          <div className="text-center">
            <Status
              icon={<AlertTriangle size={30} />}
              title="A fizetés állapotát nem sikerült megerősíteni"
              text="Kérjük, ne indíts újabb fizetést. Ellenőrizd újra az állapotot, vagy vedd fel velünk a kapcsolatot, és segítünk ellenőrizni a foglalásodat."
            />
            {result.data?.booking_id && (
              <p className="mt-4 text-xs text-[var(--ink-soft)] break-all">Foglalási azonosító: {result.data.booking_id}</p>
            )}
            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="tk-btn-primary justify-center min-h-11"
              >
                Újraellenőrzés
              </button>
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tk-btn-secondary justify-center min-h-11"
              >
                Kapcsolat
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Status({ icon, title, text }) {
  return (
    <>
      <span
        aria-hidden="true"
        className="w-16 h-16 mx-auto rounded-full bg-[rgba(110,30,43,0.08)] text-[var(--burgundy)] flex items-center justify-center"
      >
        {icon}
      </span>
      <h2 className="font-serif text-3xl sm:text-4xl mt-6 text-[var(--ink)]">{title}</h2>
      <p className="mt-4 text-[var(--ink-soft)] leading-relaxed max-w-xl mx-auto">{text}</p>
    </>
  );
}

function Tile({ label, value, strong = false }) {
  return (
    <div className="rounded-2xl bg-[var(--beige-50)] p-4 min-w-0">
      <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">
        {label}
      </p>
      <p className={`mt-2 break-words text-[var(--ink)] ${strong ? "font-serif text-xl text-[var(--burgundy)]" : "text-sm font-medium"}`}>
        {value}
      </p>
    </div>
  );
}

function NextStep({ number, title, text }) {
  return (
    <div className="flex items-start gap-3 text-left">
      <span className="w-8 h-8 shrink-0 rounded-full bg-[rgba(110,30,43,0.08)] text-[var(--burgundy)] flex items-center justify-center text-xs font-semibold" aria-hidden="true">
        {number}
      </span>
      <div>
        <p className="font-medium text-sm text-[var(--ink)]">{title}</p>
        <p className="mt-1 text-sm text-[var(--ink-soft)] leading-relaxed">{text}</p>
      </div>
    </div>
  );
}
